import { makeAutoObservable, runInAction } from 'mobx';
import {
  checkoutApi,
  OperationConflictError,
  QuoteChangedError,
  type CreatedSale,
  type QuoteRequest,
} from '@/api/checkout';
import type { DeliveryArea } from '@/api/areas';
import {
  clearDraft,
  loadDraft,
  saveDraft,
  type CartDraft,
  type CartDraftLine,
} from '@/lib/draft-storage';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import {
  // Aliased: the store exposes its own `changeMinor` getter, and two things by
  // one name in one file is a re-reading tax nobody should have to pay.
  changeMinor as computeChangeMinor,
  formatMinor,
  parseMinor,
  toMinor,
  toRupees,
  type Minor,
} from '@/lib/money';
import { loadJson } from '@/lib/storage';
import { AUTH_STORAGE_KEY } from '@/stores/AuthStore';
import {
  requiredFieldsFor,
  type CompletedOrder,
  type CreateOrderPayload,
  type DeliveryPricing,
  type OrderField,
  type OrderItemPayload,
  type OrderQuote,
  type OrderRound,
  type OrderTypeWire,
  type PaymentMethodLabel,
  type PaymentSummary,
} from '@/types/checkout';
import {
  displayStock,
  effectivePriceRupees,
  type AddonsConfigGroup,
  type Product,
} from '@/types/products';

/**
 * CartStore — ticket state and the single checkout submission path.
 *
 * MONEY (V2.2-01). Every amount here is integer paisa. The old store mixed
 * floored display values, a ceilinged "Exact" and float subtraction for change,
 * which is how one sale showed total Rs 6,089, cash Rs 6,090 and change Rs 0.
 * Local totals are still DISPLAY-ONLY — the server reprices everything — but
 * they no longer contradict each other while the cashier reads them.
 *
 * ORDER TYPE (V2.2-02). Which fields exist is decided by the order type, and
 * the payload builder sends only what that type may carry:
 *
 *   dine-in   table required; no customer identity is collected at all
 *   pickup    name and phone OPTIONAL; blank is a real walk-in, not a customer
 *   delivery  name, phone, street and area all required
 *
 * Switching type CLEARS what cannot apply, so stale state from a previous type
 * can never leak into a payload.
 *
 * SUBMISSION.
 * - ONE in-flight guard lives HERE, not in a button, so double tap/click/Enter
 *   cannot produce two POSTs.
 * - The cart owns a `clientOrderId` from its first line until an authoritative
 *   response arrives, and it is persisted with the draft. That — not the button
 *   lock — is the integrity guarantee: a retry after a lost response returns
 *   the ORIGINAL order rather than creating a second one.
 * - Tender opens on a server quote. If the price moves before the create
 *   commits, the server answers 409 and the cashier confirms the new total;
 *   nothing is ever charged at one amount and banked at another.
 * - On success the authoritative order replaces all local amounts.
 *
 * Draft recovery: the ticket (and its operation key) is mirrored into
 * localStorage on every line/order-type mutation. No customer PII and no free
 * text ever reaches storage — see src/lib/draft-storage.ts.
 */

export interface CartAddon {
  /** Addon product id as string — one addon row per product per line. */
  key: string;
  product: Product;
  quantity: number;
}

export interface CartLine {
  key: string;
  /** For a variant this IS the variant row — its id goes in product_id. */
  product: Product;
  /** Variable parent (display + addon offering context), when applicable. */
  parent: Product | null;
  quantity: number;
  /** Selected option metadata; persisted verbatim as item attributes. */
  attributes: Record<string, string>;
  /** Item note — serialized as attributes.notes (the only path that round-trips). */
  note: string;
  addons: CartAddon[];
  /** Display-only unit price in paisa; the server reprices every item. */
  unitPriceMinor: Minor;
  /** Addon offering — variants inherit their PARENT's addons_config. */
  addonsConfig: AddonsConfigGroup[] | null;
}

/**
 * One line's display total, by the SAME rule `subtotalMinor` sums.
 *
 * It lives beside that getter, and is exported, because both tickets in this
 * console — the register's and the order editor's — render a per-line amount
 * and each had grown its own copy of the arithmetic. Two copies of a money rule
 * is how a line total comes to disagree with the total printed under it, and
 * `effectivePriceRupees` (rather than a local `compare_at_price || price`) is
 * the half that drifts first. Display only: the server reprices every item.
 */
export function lineTotalMinor(line: CartLine): number {
  return (
    line.unitPriceMinor * line.quantity +
    line.addons.reduce((sum, a) => sum + toMinor(effectivePriceRupees(a.product)) * a.quantity, 0)
  );
}

/**
 * The meta line under a ticket line's name: chosen options, then addons, then
 * the item note.
 *
 * `attributes.notes` is FILTERED because a note is not an option. It appears
 * under that key only because `buildItems` puts it there — the one path that
 * round-trips a note to the server — and it is already rendered from
 * `line.note` at the end of this same string. The editor's copy of this helper
 * had no such filter, so any attributes map arriving with the wire's own `notes`
 * key printed the note TWICE: once as an unlabelled option, once quoted.
 *
 * The two copies had already drifted in what they SAY, too: the editor rendered
 * raw attribute slugs (`size: Large`) where the register renders the value
 * alone. The register's rule is the one kept — the cashier who built the order
 * and the operator correcting it should not be reading two descriptions of the
 * same line.
 */
export function lineMeta(line: CartLine): string {
  const bits: string[] = [];
  const options = Object.entries(line.attributes)
    .filter(([key]) => key !== 'notes')
    .map(([, value]) => value);
  if (options.length > 0) bits.push(options.join(' / '));
  for (const addon of line.addons) bits.push(`+ ${addon.quantity} × ${addon.product.name}`);
  const note = line.note.trim();
  if (note) bits.push(`"${note}"`);
  return bits.join(' · ');
}

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error' | 'ambiguous';

/** Tender cannot open until the server has priced the ticket. */
export type QuoteStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface CompletedSale {
  /** Authoritative server order — receipt totals come from here only. */
  order: CompletedOrder;
  /** Server payment summary (cash received, change). Null for card/mobile. */
  payment: PaymentSummary | null;
  /** The accepted round, for the kitchen ticket. */
  round: OrderRound | null;
}

/**
 * A shipping zone rate the cashier picked — retail's alternative to an area.
 *
 * Flattened out of the zone, not a reference into a loaded list, for the same
 * reason `SelectedTable` is: the ticket has to survive the picker unmounting
 * and the zone list being refetched, and the two things the ticket needs to say
 * about the choice are what it is called and which zone it came from. The
 * SETTINGS are deliberately absent — the rate is priced by the server
 * (OrderCreationService.php:702-716), so a copy of `cost`/`quantity_tiers` here
 * would only be a second, drifting implementation of the postage.
 */
export interface SelectedShippingMethod {
  /** ShippingZoneMethod id — this is the `shipping_method_id` on the wire. */
  id: number;
  zoneId: number;
  zoneName: string;
  title: string;
}

/** The table a dine-in ticket is seated at. Chosen before anything can be sent. */
export interface SelectedTable {
  id: number;
  label: string;
  /** The tab already open on this table, if any — an append rather than a seat. */
  openOrderId: number | null;
}

function lineKey(product: Product, attributes: Record<string, string>): string {
  return `${product.id}:${JSON.stringify(attributes)}`;
}

/**
 * Operation id for one ticket.
 *
 * `crypto.randomUUID` where available; the fallback exists because it is absent
 * on insecure origins, and a counter on a plain-HTTP LAN address still needs a
 * key or it loses every duplicate-safety guarantee this store depends on.
 */
function newOperationId(): string {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  const random = Math.random().toString(36).slice(2);
  return `op-${Date.now().toString(36)}-${random}`;
}

/** Resolves a persisted product id against the LIVE catalog (never a snapshot). */
export type DraftProductResolver = (productId: number) => Product | null | undefined;

export interface DraftRestoreResult {
  restored: boolean;
  /** Lines dropped because the product is gone from the live catalog. */
  droppedLines: number;
  /** Draft was saved mid-AMBIGUOUS submit — reconcile against the Queue first. */
  needsReconciliation: boolean;
}

export interface CartStoreOptions {
  /** Stock already reserved by the order being edited; absent on new POS tickets. */
  stockCredit?: (productId: number) => number;
  /**
   * Identity the draft belongs to. Returning null disables draft persistence
   * entirely (nothing is written, nothing restores).
   */
  getScopeKey?: () => string | null;
}

/**
 * Default draft scope — user + branch + terminal, so a draft can never cross a
 * branch/terminal switch or a different cashier on a shared counter device.
 */
function defaultDraftScopeKey(): string | null {
  const snapshot = loadJson<{
    user?: { id?: unknown };
    organization?: { id?: unknown } | null;
    branch?: { id?: unknown } | null;
    terminal?: { id?: unknown } | null;
  }>(AUTH_STORAGE_KEY);
  const userId = snapshot?.user?.id;
  // No identity means no safe owner for the draft — persist nothing.
  if (typeof userId !== 'number') return null;
  const org = typeof snapshot?.organization?.id === 'number' ? snapshot.organization.id : 'none';
  const branch = typeof snapshot?.branch?.id === 'number' ? snapshot.branch.id : 'none';
  const terminal = typeof snapshot?.terminal?.id === 'number' ? snapshot.terminal.id : 'none';
  return `${userId}:${org}:${branch}:${terminal}`;
}

export class CartStore {
  lines: CartLine[] = [];
  orderType: OrderTypeWire = 'pickup';

  /** Optional on pickup, required on delivery, never collected on dine-in. */
  customerName = '';
  customerPhone = '';
  /** Set when a lookup result was chosen — links the order to that customer. */
  selectedCustomerId: number | null = null;
  /** Delivery street snapshot. Stored on the order, not in an address book. */
  street = '';

  /** Dine-in only. */
  table: SelectedTable | null = null;
  covers: number | null = null;

  /** Manual POS discount in paisa; the server applies it after eligible promotions. */
  discountMinor: Minor = 0;
  /** Entered codes only; automatic promotions are discovered by the server. */
  promotionCodes: string[] = [];
  orderNotes = '';
  /** Selected delivery area (delivery only); carries its own charge. */
  area: DeliveryArea | null = null;

  /**
   * WHICH of the two delivery inputs this register collects.
   *
   * Set from the organization's business profile by the surface that has auth
   * (TicketPane calls `setDeliveryPricing(deliveryPricingFor(orgType))`), never
   * read from storage here. Two reasons it is pushed rather than pulled:
   *
   *  1. A store that reached into the persisted auth snapshot on every
   *     `fieldIssues` read would be doing a JSON.parse per keystroke, inside a
   *     MobX computed, off a value MobX cannot observe — so it would also be
   *     wrong for exactly as long as nothing else happened to re-render.
   *  2. The DEFAULT has to be the restaurant answer. Every other CartStore in
   *     this console — the order editor's (OrderEditStore.ts), the ones unit
   *     tests construct bare — must keep behaving exactly as it does today, and
   *     "unset means areas" is what guarantees that structurally rather than by
   *     each caller remembering.
   */
  deliveryPricing: DeliveryPricing = 'area';

  /**
   * Selected shipping zone rate (retail delivery only).
   *
   * The counterpart of `area`, and never held at the same time —
   * `setDeliveryPricing` clears whichever one has stopped applying.
   */
  shippingMethod: SelectedShippingMethod | null = null;

  /**
   * Shipping already charged on an EXISTING order, in paisa.
   *
   * Zero on the register — a new ticket has no history and the server prices it
   * at tender. It exists for the order EDITOR, and it exists because without it
   * a correction misreports money.
   *
   * A retail shipping correction cannot re-price postage: `OrderEditPayload`
   * carries no `shipping_method_id` (src/types/order-edit.ts:40-58), so the
   * recorded charge is exactly what the order will keep. But
   * `deliveryChargeMinor` refuses to guess a zone rate and answers 0 until a
   * quote exists — and a correction never quotes. So the draft total came out
   * SHORT BY THE POSTAGE against the original, and `TotalsDelta` read that
   * difference as money owed back to a customer who is owed nothing.
   *
   * Seeded from the order's own `deliveryCharges` (OrderEditStore.seedFrom), so
   * it is the server's figure being carried forward rather than a second
   * calculation of it. It is deliberately NOT consulted in `area` mode: there
   * the picked area's own charge is authoritative and already agrees with the
   * server by construction.
   */
  recordedDeliveryMinor: Minor = 0;
  paymentMethod: PaymentMethodLabel = 'cash';
  /** Reference printed by the external card terminal; never full card details. */
  paymentReference = '';
  /** Cash counted out, in paisa. */
  paidMinor: Minor = 0;

  /** Server price the tender screen opens on. */
  quote: OrderQuote | null = null;
  quoteStatus: QuoteStatus = 'idle';
  quoteError: ApiError | null = null;

  submitStatus: SubmitStatus = 'idle';
  submitError: ApiError | null = null;
  completedSale: CompletedSale | null = null;

  /**
   * The restored ticket was mid-AMBIGUOUS submit before the reload — the order
   * may already exist. UI must send the cashier to the Queue before charging.
   */
  draftNeedsReconciliation = false;

  /**
   * This ticket's operation id. Minted with the first line, kept across
   * failures and reloads, and only discarded once an authoritative response has
   * been seen. Reusing it is what makes a retry safe; replacing it early is
   * what would make one dangerous.
   */
  private clientOrderId: string | null = null;

  private options: CartStoreOptions;
  /** Suppresses per-mutation writes while restoreDraft() rebuilds the ticket. */
  private restoring = false;
  private quoteEpoch = 0;
  private quoteRequestKey: string | null = null;

  constructor(options: CartStoreOptions = {}) {
    this.options = options;
    makeAutoObservable<CartStore, 'options' | 'restoring' | 'clientOrderId' | 'quoteEpoch' | 'quoteRequestKey'>(this, {
      options: false,
      restoring: false,
      clientOrderId: false,
      quoteEpoch: false,
      quoteRequestKey: false,
    });
  }

  // ── line management ──────────────────────────────────────────────────────

  /**
   * Add a sellable row (simple product or variant row). Same product with the
   * same attributes merges into one line. For variants pass the variable
   * parent so its addons_config travels with the line.
   */
  addProduct(
    product: Product,
    attributes: Record<string, string> = {},
    parent?: Product,
    quantity = 1,
  ): string {
    const key = lineKey(product, attributes);
    // Refuse the unit the server would refuse anyway. Without this the ticket
    // could hold 1200 of a product with 982 in stock, and the cashier only
    // found out when the whole sale came back 422 at tender.
    const remaining = this.stockRemaining(product);
    if (remaining === 0) return key;
    const wanted = Math.max(1, Math.floor(quantity));
    const addition = remaining === null ? wanted : Math.min(wanted, remaining);
    const existing = this.lines.find((l) => l.key === key);
    if (existing) {
      existing.quantity += addition;
      this.afterTicketChange();
      return key;
    }
    this.lines.push({
      key,
      product,
      parent: parent ?? null,
      quantity: addition,
      attributes,
      note: '',
      addons: [],
      unitPriceMinor: toMinor(effectivePriceRupees(product)),
      addonsConfig: product.addons_config?.length
        ? product.addons_config
        : (parent?.addons_config ?? null),
    });
    this.afterTicketChange();
    return key;
  }

  findLine(key: string): CartLine | null {
    return this.lines.find((l) => l.key === key) ?? null;
  }

  increment(key: string): void {
    const line = this.findLine(key);
    if (!line) return;
    if (this.stockRemaining(line.product) === 0) return;
    line.quantity += 1;
    this.afterTicketChange();
  }

  decrement(key: string): void {
    const line = this.findLine(key);
    if (!line) return;
    if (line.quantity > 1) {
      line.quantity -= 1;
      this.afterTicketChange();
    } else this.removeLine(key);
  }

  removeLine(key: string): void {
    this.lines = this.lines.filter((l) => l.key !== key);
    this.afterTicketChange();
  }

  setLineQuantity(key: string, quantity: number): void {
    const line = this.findLine(key);
    if (!line) return;
    if (quantity <= 0) this.removeLine(key);
    else {
      const wanted = Math.floor(quantity);
      const stock = displayStock(line.product);
      // Typed/pasted quantities are clamped rather than refused: the cashier
      // asked for a number, and the largest sellable one is the useful answer.
      line.quantity = stock === null ? wanted : Math.max(1, Math.min(wanted, stock));
      this.afterTicketChange();
    }
  }

  /** Item note — free text, so it is intentionally NOT persisted to the draft. */
  setLineNote(key: string, note: string): void {
    const line = this.findLine(key);
    if (line) {
      line.note = note;
      // A note does not change the price, so no re-quote — but it does change
      // the payload, so the ticket is no longer the one that was quoted only if
      // the server prices notes, which it does not.
    }
  }

  setAddonQuantity(lineKeyArg: string, addonProduct: Product, quantity: number): void {
    const line = this.findLine(lineKeyArg);
    if (!line) return;
    const addonKey = String(addonProduct.id);
    const existing = line.addons.find((a) => a.key === addonKey);
    if (quantity <= 0) {
      line.addons = line.addons.filter((a) => a.key !== addonKey);
      this.afterTicketChange();
      return;
    }
    if (existing) existing.quantity = Math.floor(quantity);
    else line.addons.push({ key: addonKey, product: addonProduct, quantity: Math.floor(quantity) });
    this.afterTicketChange();
  }

  /** In-cart quantity for a product id (variants counted under their own id). */
  /**
   * Ticket quantities indexed by product id, in ONE pass over the lines.
   *
   * `quantityInCart` and `sellableQuantity` each used to walk every line on
   * every call, and the register grid calls both once per card. With a 234-card
   * menu and a twenty-line ticket that is ~9,400 iterations per render of the
   * pane — every time the cashier taps anything.
   *
   * Two indexes because the two counts genuinely differ: `folded` credits a
   * variant's units to its parent as well, so a parent card can show a combined
   * bubble; `sellable` does not, because stock is held per sellable row and
   * Large must not eat Small's count.
   */
  private get quantityIndex(): { folded: Map<number, number>; sellable: Map<number, number> } {
    const folded = new Map<number, number>();
    const sellable = new Map<number, number>();
    const add = (map: Map<number, number>, id: number, n: number) =>
      map.set(id, (map.get(id) ?? 0) + n);
    for (const line of this.lines) {
      add(sellable, line.product.id, line.quantity);
      add(folded, line.product.id, line.quantity);
      // `||` in the original: a line counts ONCE toward the parent even though
      // both branches could match, so guard against double-counting here.
      if (line.parent && line.parent.id !== line.product.id) {
        add(folded, line.parent.id, line.quantity);
      }
    }
    return { folded, sellable };
  }

  quantityInCart(productId: number): number {
    return this.quantityIndex.folded.get(productId) ?? 0;
  }

  // ── stock ────────────────────────────────────────────────────────────────
  //
  // The server is the authority on stock and rejects an item whose quantity
  // exceeds `stock_quantity` (OrderCreationService.php:511-515). The ticket
  // holds the SAME rule so that a cashier meets the limit while adding items,
  // at the product they are adding, instead of meeting it as a rejected sale
  // in front of the guest. Nothing here decides an order's fate — it only
  // stops the register from building one that is already known to fail.

  /**
   * Units of a SELLABLE row already on the ticket.
   *
   * Deliberately not `quantityInCart`: that folds variants into their parent so
   * the parent's card can show a combined bubble, which is right for a card and
   * wrong for a limit. Stock is held per sellable row, so Large and Small draw
   * down separate counts.
   */
  private sellableQuantity(productId: number): number {
    return this.quantityIndex.sellable.get(productId) ?? 0;
  }

  /**
   * Stock left to sell after what the ticket already holds. Null when the
   * product carries no stock figure, which means untracked, not zero.
   *
   * Clamped at 0: a ticket restored from a draft, or a product whose stock fell
   * while it sat in the cart, can legitimately be over the line already, and a
   * negative "left" figure is not something to put on a card.
   */
  stockRemaining(product: Product): number | null {
    const stock = displayStock(product);
    if (stock === null) return null;
    const held =
      product.type === 'variable'
        ? this.quantityInCart(product.id) // parent card: its variants draw it down
        : this.sellableQuantity(product.id);
    return Math.max(0, stock + (this.options.stockCredit?.(product.id) ?? 0) - held);
  }

  /**
   * The largest quantity ONE line may hold, given what the rest of the ticket
   * has already claimed. Null when the product is untracked.
   *
   * `excludeLineKey` is what makes this usable while editing an existing line:
   * that line's own quantity is about to be replaced, so counting it against
   * itself would shrink its ceiling every time the sheet was reopened.
   */
  maxLineQuantity(product: Product, excludeLineKey?: string): number | null {
    const stock = displayStock(product);
    if (stock === null) return null;
    let held = 0;
    for (const line of this.lines) {
      if (line.key === excludeLineKey) continue;
      if (line.product.id === product.id) held += line.quantity;
    }
    return Math.max(0, stock + (this.options.stockCredit?.(product.id) ?? 0) - held);
  }

  /**
   * Lines that have taken all the stock there is, for the one-line notice under
   * the cart that explains why `+` stopped responding. Derived, so it clears
   * itself the moment the quantity comes down.
   */
  get stockCappedLines(): Array<{ key: string; name: string; stock: number }> {
    const capped: Array<{ key: string; name: string; stock: number }> = [];
    for (const line of this.lines) {
      const available = displayStock(line.product);
      if (available === null) continue;
      const stock = available + (this.options.stockCredit?.(line.product.id) ?? 0);
      if (this.sellableQuantity(line.product.id) >= stock) {
        capped.push({ key: line.key, name: line.product.name, stock });
      }
    }
    return capped;
  }

  // ── field setters ────────────────────────────────────────────────────────

  /**
   * Switching type clears everything the new type cannot carry.
   *
   * Without this, a delivery address typed by mistake would ride along on a
   * dine-in order, or a table chosen and then abandoned would seat a pickup —
   * both rejected by the server, but only after the guest is standing there.
   */
  setOrderType(type: OrderTypeWire): void {
    if (this.orderType === type) return;
    this.orderType = type;

    if (type === 'dinein') {
      this.clearCustomerFields();
      this.area = null;
      this.shippingMethod = null;
      this.street = '';
    } else {
      this.table = null;
      this.covers = null;
      if (type === 'pickup') {
        this.area = null;
        // Same rule as the area beside it: a rate chosen for a shipping order
        // and then abandoned must not ride along on a walk-in, where the server
        // would price postage on an order nobody is delivering.
        this.shippingMethod = null;
        this.street = '';
      }
    }

    this.invalidateQuote();
    this.persistDraft();
  }

  setCustomerName(v: string): void {
    this.customerName = v;
    // Typing over a chosen customer un-links them: the order must not attach a
    // stable customer_id to a name the cashier has since replaced.
    if (this.selectedCustomerId !== null) this.invalidateQuote();
    this.selectedCustomerId = null;
  }

  setCustomerPhone(v: string): void {
    if (this.customerPhone !== v || this.selectedCustomerId !== null) this.invalidateQuote();
    this.customerPhone = v;
    this.selectedCustomerId = null;
  }

  /** Fill the ticket from a lookup result, keeping the stable customer link. */
  applyCustomer(customer: { id: number; name: string; phone: string }): void {
    if (this.selectedCustomerId !== customer.id) this.invalidateQuote();
    this.selectedCustomerId = customer.id;
    if (customer.name) this.customerName = customer.name;
    this.customerPhone = customer.phone;
  }

  setStreet(v: string): void {
    this.street = v;
  }

  setTable(table: SelectedTable | null): void {
    this.table = table;
    this.persistDraft();
  }

  setCovers(covers: number | null): void {
    this.covers = covers && covers > 0 ? Math.floor(covers) : null;
  }

  /** Discount in RUPEES from the UI; stored as paisa. */
  setDiscountAmount(rupees: number): void {
    this.discountMinor = Number.isFinite(rupees) ? Math.max(0, toMinor(rupees)) : 0;
    this.invalidateQuote();
  }

  setPromotionCodes(value: string): void {
    this.promotionCodes = Array.from(
      new Set(
        value
          .split(',')
          .map((code) => code.trim().toUpperCase())
          .filter(Boolean),
      ),
    ).slice(0, 6);
    this.invalidateQuote();
  }

  setOrderNotes(v: string): void {
    this.orderNotes = v;
  }

  setArea(area: DeliveryArea | null): void {
    this.area = area;
    this.invalidateQuote();
  }

  /**
   * Adopt the organization's delivery pricing model, dropping the input that
   * has stopped applying.
   *
   * Idempotent, because the caller is a render effect and will hand back the
   * same value on every pass. The clear is not optional: without it, switching
   * organization on a shared device could leave a restaurant area attached to a
   * ticket a shop is about to send, and `buildOrderPayload` would then be
   * choosing between two live answers instead of reading the one that applies.
   */
  setDeliveryPricing(pricing: DeliveryPricing): void {
    if (this.deliveryPricing === pricing) return;
    this.deliveryPricing = pricing;
    if (pricing === 'area') this.shippingMethod = null;
    else this.area = null;
    this.invalidateQuote();
  }

  setShippingMethod(method: SelectedShippingMethod | null): void {
    this.shippingMethod = method;
    // Postage is part of the price, so the held quote is no longer this ticket's.
    this.invalidateQuote();
  }

  setPaymentMethod(method: PaymentMethodLabel): void {
    this.paymentMethod = method;
    if (method !== 'card') this.paymentReference = '';
  }

  setPaymentReference(reference: string): void {
    this.paymentReference = reference;
  }

  /** Cash received in RUPEES from the keypad; stored as paisa. */
  setPaidAmount(rupees: number): void {
    this.paidMinor = Number.isFinite(rupees) ? Math.max(0, toMinor(rupees)) : 0;
  }

  setPaidMinor(minor: Minor): void {
    this.paidMinor = Math.max(0, Math.round(minor));
  }

  // ── display-only computeds (server totals are authoritative) ─────────────

  get itemCount(): number {
    return this.lines.reduce(
      (sum, l) => sum + l.quantity + l.addons.reduce((s, a) => s + a.quantity, 0),
      0,
    );
  }

  get subtotalMinor(): Minor {
    return this.lines.reduce((sum, l) => sum + lineTotalMinor(l), 0);
  }

  /**
   * The postage on the ticket, in paisa.
   *
   * An area carries its own flat charge, so the restaurant path can show it the
   * moment it is picked — that figure and the server's agree by construction.
   *
   * A ZONE RATE CANNOT BE SHOWN THAT WAY, and pretending otherwise is the bug
   * this branch exists to avoid. A ShippingZoneMethod may be flat
   * (`settings.cost`) or banded by total quantity (`settings.quantity_tiers`,
   * OrderCreationService.php:706-716), and reimplementing the banding here
   * would be a second copy of the postage that drifts the first time a tier is
   * edited. So retail shows the SERVER's number or none at all: the quote's
   * `delivery_charges` once tender has priced the ticket, and `0` beforehand —
   * which `deliveryChargePending` below tells the pane not to render as "Rs 0".
   */
  get deliveryChargeMinor(): Minor {
    if (this.orderType !== 'delivery') return 0;
    if (this.quote) return parseMinor(this.quote.deliveryCharges);
    if (this.deliveryPricing === 'shipping-method') {
      // No quote yet. On a NEW ticket that means "not priced" and 0 is honest —
      // `deliveryChargePending` stops the pane rendering it as "Rs 0". On a
      // CORRECTION it means the postage this order already carries, which a PUT
      // cannot change, so carrying it is the only figure that makes the totals
      // delta describe the real difference. Zero on the register, because
      // nothing seeds it there.
      return this.recordedDeliveryMinor;
    }
    return this.area ? parseMinor(this.area.delivery_charges) : 0;
  }

  /**
   * A rate is chosen but nobody has priced it yet.
   *
   * The one state where the ticket's own delivery figure would be a lie rather
   * than a number: `deliveryChargeMinor` returns 0 because it refuses to guess,
   * and a row reading "Delivery Rs 0" beside a server total that includes
   * postage is worse than a row that admits it does not know yet.
   */
  get deliveryChargePending(): boolean {
    return (
      this.orderType === 'delivery' &&
      this.deliveryPricing === 'shipping-method' &&
      this.shippingMethod != null &&
      this.quote == null &&
      // A correction already knows the figure — it is the one the order carries
      // — so the ticket shows it rather than admitting it does not know.
      this.recordedDeliveryMinor === 0
    );
  }

  /** Mirrors the server clamp min(discount, subtotal). Display-only. */
  get appliedDiscountMinor(): Minor {
    return Math.min(this.discountMinor, this.subtotalMinor);
  }

  /** Server promotion + manual savings once quoted; local manual saving before then. */
  get displayDiscountMinor(): Minor {
    return this.quote ? parseMinor(this.quote.discountAmount) : this.appliedDiscountMinor;
  }

  /**
   * Local total, for the ticket only.
   *
   * The tender screen and the receipt use the SERVER quote/order instead — this
   * is what the cashier watches while building the ticket, not what they charge.
   */
  get totalMinor(): Minor {
    return Math.max(0, this.subtotalMinor + this.deliveryChargeMinor - this.appliedDiscountMinor);
  }

  get displayTotalMinor(): Minor {
    return this.quote ? parseMinor(this.quote.total) : this.totalMinor;
  }

  /** The amount tender is actually taken against: the server quote when held. */
  get payableMinor(): Minor {
    return this.quote ? parseMinor(this.quote.total) : this.totalMinor;
  }

  get changeMinor(): Minor {
    return computeChangeMinor(this.paidMinor, this.payableMinor);
  }

  /** Rupee views for the few call sites that still speak in rupees. */
  get subtotal(): number {
    return toRupees(this.subtotalMinor);
  }
  get deliveryCharge(): number {
    return toRupees(this.deliveryChargeMinor);
  }
  get appliedDiscount(): number {
    return toRupees(this.appliedDiscountMinor);
  }
  get total(): number {
    return toRupees(this.totalMinor);
  }

  get formattedTotal(): string {
    return formatMinor(this.displayTotalMinor);
  }

  // ── order-type validation ────────────────────────────────────────────────

  /**
   * Field-level reasons this ticket cannot be sent, keyed like a 422 so the UI
   * can point at the empty box rather than say "invalid order".
   *
   * ONE predicate, used by both the button state and the payload builder. The
   * offline POS this behaviour is modelled on computed validity in one place
   * and then rejected the order somewhere else, which is how a filled-in
   * delivery form could still refuse to send.
   */
  get fieldIssues(): Record<string, string> {
    const issues: Record<string, string> = {};

    if (this.orderType === 'dinein') {
      if (!this.table) issues.table_id = 'Choose a table before sending this order.';
      return issues;
    }

    if (this.orderType === 'delivery') {
      /*
        DRIVEN OFF THE DECLARED MATRIX, not off a second list written here.

        `ORDER_TYPE_REQUIRED_FIELDS` (src/types/checkout.ts) exists so the rule
        can be asserted outside the register, and src/types/__tests__/checkout.test.ts
        does assert it — but only this loop makes that assertion cover anything
        that sells. A hand-written chain of `if`s beside a table nobody reads is
        the same shape as the `orderTypeOptions` copy TicketPane used to keep:
        a divergence that no test can fail, only a guest.

        `requiredFieldsFor` is the one place the retail substitution lives —
        `shipping_method_id` in place of `area_id`, never both. pos-api agrees
        from the other side: `area_id` is required on delivery only when no
        `shipping_method_id` was supplied (OrderController.php:161-168). What
        matters on both sides is that a delivery is never recorded with nobody
        having priced it, because calculateOrderTotals leaves `delivery_charges`
        at 0 when neither input arrives and the order ships free without anyone
        having decided that.

        Keys match the server's own 422 keys, so `fieldError('area_id')` in the
        pane points at the same box whichever side did the refusing.
      */
      const filled: Record<OrderField, boolean> = {
        table_id: this.table != null,
        customer_name: this.customerName.trim().length > 0,
        customer_phone: this.customerPhone.trim().length > 0,
        street: this.street.trim().length > 0,
        area_id: this.area != null,
        shipping_method_id: this.shippingMethod != null,
      };
      const messages: Record<OrderField, string> = {
        table_id: 'Choose a table before sending this order.',
        customer_name: 'A delivery order needs a name.',
        customer_phone: 'A delivery order needs a phone number.',
        street: 'A delivery order needs a street address.',
        area_id: 'Choose a delivery area.',
        shipping_method_id: 'Choose a shipping method.',
      };
      for (const field of requiredFieldsFor('delivery', this.deliveryPricing)) {
        if (!filled[field]) issues[field] = messages[field];
      }
    }

    // Pickup deliberately has no identity requirement: a blank ticket is a real
    // walk-in, and demanding a phone is what pushed counters to a dummy number.
    return issues;
  }

  get isValidForSubmit(): boolean {
    return this.lines.length > 0 && Object.keys(this.fieldIssues).length === 0;
  }

  get canCheckout(): boolean {
    return (
      this.isValidForSubmit &&
      this.submitStatus !== 'submitting' &&
      // A recovered ticket that was mid-send may already be an order. It stays
      // uncheckoutable until the cashier confirms they checked the Queue —
      // warning without blocking still lets a busy counter double-charge.
      !this.draftNeedsReconciliation
    );
  }

  get isSubmitting(): boolean {
    return this.submitStatus === 'submitting';
  }

  /** Dine-in tenders at settlement, so its primary action sends a round instead. */
  get isSendOnly(): boolean {
    return this.orderType === 'dinein';
  }

  // ── payload ──────────────────────────────────────────────────────────────

  /** The items array, with addons following their parent by string index. */
  buildItems(): OrderItemPayload[] {
    const items: OrderItemPayload[] = [];
    for (const line of this.lines) {
      const attributes: Record<string, string> = { ...line.attributes };
      const note = line.note.trim();
      if (note) attributes.notes = note;
      items.push({
        product_id: line.product.id,
        quantity: line.quantity,
        attributes,
      });
      const parentIndex = items.length - 1;
      for (const addon of line.addons) {
        items.push({
          product_id: addon.product.id,
          quantity: addon.quantity,
          attributes: {},
          parent_key: String(parentIndex),
        });
      }
    }
    return items;
  }

  /**
   * What this ticket says a delivery costs — as wire keys, for BOTH bodies.
   *
   * Exactly one key, or none. Never both: pos-api's `area_id` rule is
   * `Rule::requiredIf(… && ! filled('shipping_method_id'))`
   * (OrderController.php:161-168), so it would happily take the pair, and
   * OrderCreationService.php:702-716 would then read the area's charge and let
   * the method overwrite it. The resulting order records a number that neither
   * of its two inputs explains on its own. See `forbiddenFieldsFor`
   * (src/types/checkout.ts).
   *
   * Empty on pickup and dine-in, where postage is not a concept.
   */
  private deliveryPricingPayload(): { area_id?: number; shipping_method_id?: number } {
    if (this.orderType !== 'delivery') return {};
    if (this.deliveryPricing === 'shipping-method') {
      return this.shippingMethod ? { shipping_method_id: this.shippingMethod.id } : {};
    }
    return this.area ? { area_id: this.area.id } : {};
  }

  /**
   * The POST /orders body for the CURRENT order type.
   *
   * Sends only what this type may carry. Both API layers reject the
   * combinations this refuses to build, so a mismatch here surfaces as a 422
   * rather than a wrong order.
   */
  buildOrderPayload(daySessionId?: number | null): CreateOrderPayload {
    const payload: CreateOrderPayload = {
      order_type: this.orderType,
      payment_method: this.paymentMethod,
      items: this.buildItems(),
      client_order_id: this.operationKey(),
    };

    if (this.orderType === 'dinein') {
      // No customer identity at all: a table is not a person, and inventing one
      // pollutes every customer total in the office.
      if (this.table) payload.table_id = this.table.id;
      // A real column upstream since V2.3-02. It used to reach the order only
      // through the tables API's openTab, so a dine-in ticket sent down THIS
      // path lost its head count entirely.
      if (this.covers != null && this.covers > 0) payload.covers = this.covers;
    } else {
      const name = this.customerName.trim();
      const phone = this.customerPhone.trim();
      if (name) payload.customer_name = name;
      if (phone) payload.customer_phone = phone;
      if (this.selectedCustomerId !== null) payload.customer_id = this.selectedCustomerId;

      if (this.orderType === 'delivery') {
        payload.shipping_address = { street: this.street.trim() };
        // The SAME keys `refreshQuote` sends, from the same function — see the
        // note there for what happens to a retail sale when they diverge.
        Object.assign(payload, this.deliveryPricingPayload());
      }
    }

    const notes = this.orderNotes.trim();
    if (notes) payload.notes = notes;
    if (daySessionId != null) payload.day_session_id = daySessionId;
    if (this.appliedDiscountMinor > 0)
      payload.discount_amount = toRupees(this.appliedDiscountMinor);
    if (this.promotionCodes.length > 0) payload.promotion_codes = [...this.promotionCodes];
    if (this.quote) payload.quote_fingerprint = this.quote.fingerprint;
    if (this.paymentMethod === 'cash' && this.paidMinor > 0) {
      payload.cash_received = toRupees(this.paidMinor);
    }
    if (this.paymentMethod === 'card' && this.paymentReference.trim()) {
      payload.payment_reference = this.paymentReference.trim();
    }

    return payload;
  }

  // ── quoting ──────────────────────────────────────────────────────────────

  /**
   * Ask the server what this ticket costs, before any money is counted.
   *
   * Called when tender opens. Writes nothing server-side, so it is safe to call
   * again whenever the ticket changes.
   */
  async refreshQuote(): Promise<boolean> {
    if (this.lines.length === 0) return false;
    const key = this.quoteKey;
    if (this.quoteStatus === 'loading' && this.quoteRequestKey === key) return false;
    const epoch = ++this.quoteEpoch;
    const request = this.quoteRequest;
    this.quoteRequestKey = key;
    this.quoteStatus = 'loading';
    this.quoteError = null;

    try {
      const quote = await checkoutApi.quote(request);
      if (epoch !== this.quoteEpoch || key !== this.quoteKey) return false;
      runInAction(() => {
        this.quote = quote;
        this.quoteStatus = 'ready';
      });
      return true;
    } catch (e) {
      if (epoch !== this.quoteEpoch || key !== this.quoteKey) return false;
      runInAction(() => {
        this.quoteError = normalizeApiError(e);
        this.quoteStatus = 'error';
        this.quote = null;
      });
      return false;
    }
  }

  /** Identity of the exact quote inputs, including coupon contact identity. */
  get quoteKey(): string {
    return JSON.stringify(this.quoteRequest);
  }

  private get quoteRequest(): QuoteRequest {
    /*
      THE QUOTE'S PRICING INPUTS COME FROM THE SAME PLACE THE CREATE'S DO.

      `quote_fingerprint` makes the create refuse a ticket whose price moved
      since it was quoted (handleSubmitFailure below turns that 409 into a new
      total for the cashier to confirm). That protection inverts into a wall the
      moment the two bodies stop agreeing about their INPUTS: a retail delivery
      quoted without `shipping_method_id` and created with it is priced twice
      from different questions, the fingerprints differ by exactly the postage,
      and every single Shipping sale 409s on a ticket where nothing changed.

      So the pricing keys are built once, by `deliveryPricingPayload`, and
      spread into both bodies. Two literals that merely LOOK alike is how the
      omission got written in the first place.
    */
    return {
      order_type: this.orderType,
      items: this.buildItems(),
      ...this.deliveryPricingPayload(),
      ...(this.appliedDiscountMinor > 0
        ? { discount_amount: toRupees(this.appliedDiscountMinor) }
        : {}),
      ...(this.promotionCodes.length > 0 ? { promotion_codes: [...this.promotionCodes] } : {}),
      ...(this.selectedCustomerId !== null ? { customer_id: this.selectedCustomerId } : {}),
      ...(this.orderType !== 'dinein' && this.customerPhone.trim() ? { customer_phone: this.customerPhone.trim() } : {}),
    };
  }

  /**
   * Drop a quote the ticket has outgrown.
   *
   * Anything that changes the price invalidates it. Keeping a stale quote would
   * mean tendering against a total the ticket no longer has — the server would
   * catch it with a 409, but only after the cashier had taken the wrong money.
   */
  private invalidateQuote(): void {
    this.quoteEpoch += 1;
    this.quoteRequestKey = null;
    this.quote = null;
    this.quoteStatus = 'idle';
    this.quoteError = null;
  }

  // ── submission (single locked path) ──────────────────────────────────────

  /**
   * POST the order exactly once. Returns true only on a confirmed success.
   *
   * While a submission is in flight every further call is a no-op. The store
   * lock stops a double click; the operation id is what stops a double ORDER.
   */
  async submitOrder(daySessionId?: number | null): Promise<boolean> {
    if (this.submitStatus === 'submitting') return false;
    if (this.lines.length === 0) return false;
    if (!this.isValidForSubmit) return false;

    this.submitStatus = 'submitting';
    this.submitError = null;
    const payload = this.buildOrderPayload(daySessionId);

    // Mark the draft as in-doubt BEFORE the request leaves, not after a failure
    // comes back. If the device dies mid-POST — tab killed, battery gone,
    // browser crash — no catch block ever runs, and a draft written "clean"
    // would come back looking safe to charge again.
    this.draftNeedsReconciliation = true;
    this.persistDraft();

    try {
      const sale = await checkoutApi.create(payload);
      runInAction(() => {
        this.applyCompletedSale(sale);
      });
      return true;
    } catch (e) {
      runInAction(() => {
        this.handleSubmitFailure(e);
      });
      return false;
    }
  }

  private applyCompletedSale(sale: CreatedSale): void {
    // Authoritative response replaces every locally computed amount.
    this.completedSale = { order: sale.order, payment: sale.payment, round: sale.round };
    this.submitStatus = 'success';
    this.clearAfterSale();
  }

  private handleSubmitFailure(e: unknown): void {
    if (e instanceof QuoteChangedError) {
      // Not a rejection to fix — a new number to confirm. The ticket is intact
      // and no order exists, so the cashier re-tenders against the fresh total.
      this.quote = e.quote;
      this.quoteStatus = e.quote ? 'ready' : 'idle';
      this.paidMinor = 0;
      this.submitStatus = 'error';
      this.submitError = {
        status: 409,
        message: e.quote
          ? `The total is now ${formatMinor(parseMinor(e.quote.total))}. Confirm the new amount before charging.`
          : e.message,
        code: 'QUOTE_CHANGED',
      };
      this.draftNeedsReconciliation = false;
      this.persistDraft();
      return;
    }

    if (e instanceof OperationConflictError) {
      // The key is spent on a different order. Retrying cannot help, so the
      // cashier is sent to the Queue and the ticket keeps its doubt flag.
      this.submitStatus = 'error';
      this.submitError = { status: 409, message: e.message, code: 'IDEMPOTENCY_CONFLICT' };
      this.persistDraft();
      return;
    }

    const error = normalizeApiError(e);
    this.submitError = error;
    // status 0 = the request never got a response (network drop, timeout, or an
    // unparseable success) — the order MAY exist server-side. The cart AND its
    // operation id are preserved, so retrying resolves to the original order
    // instead of creating a second one.
    this.submitStatus = error.status === 0 ? 'ambiguous' : 'error';
    // A real HTTP status means the server answered and rejected: no order was
    // created, so the doubt raised before the request is resolved.
    if (error.status !== 0) {
      this.draftNeedsReconciliation = false;
    }
    this.persistDraft();
  }

  /** Leave the receipt/success state and start the next sale. */
  startNewSale(): void {
    this.completedSale = null;
    this.submitStatus = 'idle';
    this.submitError = null;
  }

  /**
   * Abandon the ticket in hand and start a clean one.
   *
   * The counter needs this when a guest changes their mind or walks off
   * mid-order. Until now the only way to empty a ticket was to remove every
   * line by hand, and even that left the phone, table, discount and note
   * behind, so the next guest's order started inside the last one's details.
   *
   * REFUSED while a charge is in flight or unresolved, and `false` says so.
   * `ambiguous` means the request never came back and the order MAY already
   * exist server-side; this cart and its `clientOrderId` are the only things
   * that can resolve a retry to the SAME order rather than a second one, so
   * discarding them would turn one doubtful sale into a duplicate charge nobody
   * can trace back. The button is disabled in those states too — this guard
   * makes the rule structural rather than a rendering detail.
   *
   * `completedSale` is deliberately left alone: the last-sale strip is how a
   * receipt gets reprinted, and starting the next ticket is not a reason to
   * take that away.
   */
  discardTicket(): boolean {
    if (this.isSubmitting || this.submitStatus === 'ambiguous') return false;
    this.clearAfterSale();
    this.submitStatus = 'idle';
    this.submitError = null;
    return true;
  }

  /**
   * May the operator discard right now?
   *
   * One place, so the disabled button and the refusal inside `discardTicket`
   * can never disagree about it.
   */
  get canDiscardTicket(): boolean {
    return this.hasTicketContent && !this.isSubmitting && this.submitStatus !== 'ambiguous';
  }

  /**
   * Is there anything on this ticket worth discarding?
   *
   * Not just lines: a phone number typed for a guest who then left, or a table
   * chosen for a tab that never happened, is exactly the state the discard
   * exists to clear — so an "empty" cart with those set is NOT empty.
   */
  get hasTicketContent(): boolean {
    return (
      this.lines.length > 0 ||
      this.customerName.trim() !== '' ||
      this.customerPhone.trim() !== '' ||
      this.street.trim() !== '' ||
      this.orderNotes.trim() !== '' ||
      this.table !== null ||
      this.covers !== null ||
      this.discountMinor > 0 ||
      this.promotionCodes.length > 0 ||
      this.area !== null ||
      // The retail half of the same pair. Omitting it would leave "New sale"
      // disabled on a shipping ticket whose only content is the rate — the one
      // state where the cashier has picked something and cannot unpick it.
      this.shippingMethod !== null ||
      this.paymentReference.trim() !== ''
    );
  }

  clearSubmitError(): void {
    this.submitError = null;
    if (this.submitStatus === 'error' || this.submitStatus === 'ambiguous') {
      this.submitStatus = 'idle';
    }
    // The cashier has seen and dismissed the warning; a later reload should not
    // resurrect it.
    this.draftNeedsReconciliation = false;
    this.persistDraft();
  }

  // ── draft recovery ───────────────────────────────────────────────────────

  /** Identity the draft is filed under; null disables persistence entirely. */
  getDraftScopeKey(): string | null {
    return (this.options.getScopeKey ?? defaultDraftScopeKey)();
  }

  /**
   * Rebuild the ticket persisted for `scopeKey`. EXPLICIT by design — call it
   * once the catalog is loaded, since ids are resolved against the live catalog
   * rather than restored from a price snapshot.
   *
   * Refuses to touch a cart that is mid-submit, already has lines, or is
   * showing a completed sale: recovery may never merge into, or race, a live
   * ticket.
   */
  restoreDraft(scopeKey: string, resolveProduct: DraftProductResolver): DraftRestoreResult {
    const nothing: DraftRestoreResult = {
      restored: false,
      droppedLines: 0,
      needsReconciliation: false,
    };
    if (this.submitStatus === 'submitting') return nothing;
    if (this.lines.length > 0 || this.completedSale) return nothing;

    const draft = loadDraft(scopeKey);
    if (!draft) return nothing;

    // Adopt the persisted key BEFORE any line is added, so addProduct does not
    // mint a fresh one. A recovered ticket retried under a NEW key would create
    // a second order — the exact double-charge the key exists to prevent.
    this.clientOrderId = draft.clientOrderId;

    let droppedLines = 0;
    this.restoring = true;
    try {
      for (const line of draft.lines) {
        if (!this.restoreLine(line, resolveProduct)) droppedLines += 1;
      }
    } finally {
      this.restoring = false;
    }

    if (this.lines.length === 0) {
      // Every product is gone from the catalog — the draft is unrecoverable.
      this.clientOrderId = null;
      clearDraft();
      return { restored: false, droppedLines, needsReconciliation: false };
    }

    this.orderType = draft.orderType;
    this.draftNeedsReconciliation = draft.needsReconciliation;
    // Rewrite so the stored draft matches what was actually recovered.
    this.persistDraft();
    return { restored: true, droppedLines, needsReconciliation: draft.needsReconciliation };
  }

  /** Drop the persisted draft without disturbing the current ticket. */
  discardDraft(): void {
    this.draftNeedsReconciliation = false;
    this.clearOwnDraft();
  }

  /**
   * Remove the persisted draft — but ONLY if this store owns one.
   *
   * `clearDraft()` removes a single global storage key, because there is one
   * register per device and it has exactly one ticket. That assumption broke
   * the moment a SECOND CartStore existed: the order editor holds its own
   * instance with `getScopeKey: () => null`, which disables persistence
   * entirely — and yet `reset()` on that instance was still reaching for the
   * global key and deleting the REGISTER's saved ticket. A cashier with a
   * half-built order who opened an editor and closed it again would have found
   * their recovery draft gone, with nothing to say why.
   *
   * A store that persists nothing owns nothing to clear. That is the rule, and
   * stating it here means every future caller of reset() inherits it.
   */
  private clearOwnDraft(): void {
    if (!this.getDraftScopeKey()) return;
    clearDraft();
  }

  private restoreLine(line: CartDraftLine, resolveProduct: DraftProductResolver): boolean {
    const product = resolveProduct(line.productId);
    if (!product) return false;
    const parent = line.parentId != null ? (resolveProduct(line.parentId) ?? undefined) : undefined;
    const key = this.addProduct(product, line.attributes, parent);
    // The product survived but its stock did not. `addProduct` refuses rather
    // than building a ticket the server will reject, so this counts as a
    // dropped line and the cashier is told — the same as a withdrawn product.
    if (!this.findLine(key)) return false;
    this.setLineQuantity(key, line.quantity);
    for (const addon of line.addons) {
      const addonProduct = resolveProduct(addon.productId);
      // A withdrawn addon must not block the line it belonged to.
      if (addonProduct) this.setAddonQuantity(key, addonProduct, addon.quantity);
    }
    return true;
  }

  /** Any line change re-persists the draft and drops the now-stale quote. */
  private afterTicketChange(): void {
    this.invalidateQuote();
    this.persistDraft();
  }

  /**
   * The operation id for this ticket, minting one on first use.
   *
   * A METHOD, not a getter: MobX would make a getter a computed, and a computed
   * that lazily assigns state is a side effect in a place nothing expects one.
   *
   * Deliberately lazy rather than created in the constructor, so a store that
   * is reset and never used again does not burn keys, and the id survives being
   * read many times for one ticket.
   *
   * Public because a dine-in ticket sends its round through the tables API
   * rather than through submitOrder, and that request needs the SAME key — one
   * ticket is one operation however it reaches the server.
   */
  operationKey(): string {
    this.clientOrderId ??= newOperationId();
    return this.clientOrderId;
  }

  /**
   * Mirror the ticket to storage. Called on line/order-type mutations only —
   * customer and note fields are neither persisted nor worth a write per
   * keystroke.
   */
  private persistDraft(): void {
    if (this.restoring) return;
    const scopeKey = this.getDraftScopeKey();
    if (!scopeKey) return;
    if (this.lines.length === 0) {
      clearDraft();
      return;
    }
    const draft: CartDraft = {
      scopeKey,
      orderType: this.orderType,
      clientOrderId: this.operationKey(),
      lines: this.lines.map((line) => ({
        productId: line.product.id,
        parentId: line.parent?.id ?? null,
        quantity: line.quantity,
        attributes: { ...line.attributes },
        addons: line.addons.map((addon) => ({
          productId: addon.product.id,
          quantity: addon.quantity,
        })),
      })),
      savedAt: Date.now(),
      needsReconciliation: this.draftNeedsReconciliation,
    };
    saveDraft(draft);
  }

  private clearCustomerFields(): void {
    this.customerName = '';
    this.customerPhone = '';
    this.selectedCustomerId = null;
  }

  /**
   * Post-success clear. orderType is intentionally preserved: restricted
   * terminals coerce it to an allowed value on mount, and resetting here could
   * reintroduce a type the terminal is restricted from.
   */
  private clearAfterSale(): void {
    this.lines = [];
    this.clearCustomerFields();
    this.street = '';
    this.table = null;
    this.covers = null;
    this.discountMinor = 0;
    this.promotionCodes = [];
    this.orderNotes = '';
    this.area = null;
    this.shippingMethod = null;
    // Belonged to the order that was just cleared. Left standing it would add
    // that order's postage to the next ticket's total.
    this.recordedDeliveryMinor = 0;
    this.paidMinor = 0;
    this.paymentReference = '';
    this.invalidateQuote();
    // The sale is banked, so this ticket's operation id is spent. The NEXT
    // ticket must mint its own, or a later order would replay this one's key
    // and be handed back the sale that just completed.
    this.clientOrderId = null;
    this.draftNeedsReconciliation = false;
    this.clearOwnDraft();
  }

  /**
   * Full reset — lead wires this to auth.registerScopeListener.
   *
   * `deliveryPricing` is deliberately NOT reset, for the same class of reason
   * `orderType` survives `clearAfterSale`: it is not ticket content, it is a
   * fact about the organization, and it is pushed in by a render effect
   * (TicketPane). Zeroing it here would set it back to 'area' on a retail
   * counter and the effect would not fire again — its input has not changed —
   * so the next shipping sale would be asked for a delivery area that mode has
   * no picker for. An org switch re-renders the pane with a new value and the
   * setter takes it then, which is the only moment it can legitimately change.
   */
  reset(): void {
    this.clearAfterSale();
    this.orderType = 'pickup';
    this.paymentMethod = 'cash';
    this.submitStatus = 'idle';
    this.submitError = null;
    this.completedSale = null;
  }
}
